import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Scissors,
  Clock,
  Euro,
  Trash2,
  Edit,
  User,
  Users,
  Sparkles,
  Timer,
  Hourglass,
  Palette,
  Info,
  Zap,
  Hash
} from "lucide-react";

const CATEGORIE = [
  { value: "donna", label: "Donna", icon: User, color: "bg-pink-500" },
  { value: "uomo", label: "Uomo", icon: User, color: "bg-blue-500" },
  { value: "unisex", label: "Unisex", icon: Users, color: "bg-purple-500" },
];

const TIPI = [
  { value: "taglio", label: "Taglio", hasPosa: false },
  { value: "colore", label: "Colore", hasPosa: true },
  { value: "piega", label: "Piega", hasPosa: false },
  { value: "trattamento", label: "Trattamento", hasPosa: true },
  { value: "barba", label: "Barba", hasPosa: false },
  { value: "altro", label: "Altro", hasPosa: false },
];

// Colori predefiniti per i servizi
const COLORI_SERVIZI = [
  { value: "#3B82F6", label: "Blu", bg: "bg-blue-500" },
  { value: "#EC4899", label: "Rosa", bg: "bg-pink-500" },
  { value: "#8B5CF6", label: "Viola", bg: "bg-violet-500" },
  { value: "#10B981", label: "Verde", bg: "bg-emerald-500" },
  { value: "#F59E0B", label: "Ambra", bg: "bg-amber-500" },
  { value: "#EF4444", label: "Rosso", bg: "bg-red-500" },
  { value: "#06B6D4", label: "Ciano", bg: "bg-cyan-500" },
  { value: "#84CC16", label: "Lime", bg: "bg-lime-500" },
  { value: "#F97316", label: "Arancio", bg: "bg-orange-500" },
  { value: "#6366F1", label: "Indaco", bg: "bg-indigo-500" },
];

export default function Servizi() {
  const { token } = useAuth();
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("tutti");
  const [filterTipo, setFilterTipo] = useState("tutti");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingServizio, setEditingServizio] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    categoria: "donna",
    tipo: "taglio",
    durata_minuti: 30,
    tempo_lavoro: 30,
    tempo_posa: 0,
    steps: [],
    prezzo: 0,
    colore: "#3B82F6",
    servizi_associati: [],
    giorni_richiamo: 0,
    ordine_schema: 0,
    attivo: true
  });

  // Initial data fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchServizi();
  }, []);

  const fetchServizi = async () => {
    try {
      const response = await axios.get(`${API}/servizi/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServizi(response.data);
    } catch (error) {
      if (error.response?.status === 404 || servizi.length === 0) {
        await seedServizi();
      } else {
        toast.error("Errore nel caricamento servizi");
      }
    } finally {
      setLoading(false);
    }
  };

  const seedServizi = async () => {
    try {
      await axios.post(`${API}/servizi/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const response = await axios.get(`${API}/servizi/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServizi(response.data);
      toast.success("Servizi predefiniti creati!");
    } catch {
      toast.error("Errore nella creazione dei servizi predefiniti");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione: durata_minuti = tempo_lavoro + tempo_posa
    const dataToSend = {
      ...formData,
      durata_minuti: formData.tempo_lavoro + formData.tempo_posa
    };
    
    try {
      if (editingServizio) {
        await axios.put(`${API}/servizi/${editingServizio.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Servizio aggiornato");
      } else {
        await axios.post(`${API}/servizi`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Servizio creato");
      }
      setDialogOpen(false);
      resetForm();
      setTimeout(() => fetchServizi(), 150);
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo servizio?")) return;
    try {
      await axios.delete(`${API}/servizi/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Servizio eliminato");
      setTimeout(() => fetchServizi(), 100);
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleAttivo = async (servizio) => {
    try {
      await axios.put(`${API}/servizi/${servizio.id}`, {
        attivo: !servizio.attivo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeout(() => fetchServizi(), 100);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const openEditDialog = (servizio) => {
    setEditingServizio(servizio);
    // Calcola tempo_lavoro se non presente (retrocompatibilità)
    const tempoLavoro = servizio.tempo_lavoro || (servizio.durata_minuti - (servizio.tempo_posa || 0));
    const tempoPosa = servizio.tempo_posa || 0;
    
    setFormData({
      nome: servizio.nome,
      categoria: servizio.categoria,
      tipo: servizio.tipo,
      durata_minuti: servizio.durata_minuti,
      tempo_lavoro: tempoLavoro,
      tempo_posa: tempoPosa,
      steps: servizio.steps || [],
      prezzo: servizio.prezzo,
      colore: servizio.colore || "#3B82F6",
      servizi_associati: servizio.servizi_associati || [],
      giorni_richiamo: servizio.giorni_richiamo || 0,
      ordine_schema: servizio.ordine_schema || 0,
      attivo: servizio.attivo
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingServizio(null);
    setFormData({
      nome: "",
      categoria: "donna",
      tipo: "taglio",
      durata_minuti: 30,
      tempo_lavoro: 30,
      tempo_posa: 0,
      steps: [],
      prezzo: 0,
      colore: "#3B82F6",
      servizi_associati: [],
      giorni_richiamo: 0,
      ordine_schema: 0,
      attivo: true
    });
  };

  // Quando cambia il tipo, suggerisci tempo posa
  const handleTipoChange = (tipo) => {
    const tipoInfo = TIPI.find(t => t.value === tipo);
    if (tipoInfo?.hasPosa && formData.tempo_posa === 0) {
      // Suggerisci tempo posa per colore/trattamento
      const suggestedPosa = tipo === "colore" ? 35 : 20;
      setFormData({ 
        ...formData, 
        tipo, 
        tempo_posa: suggestedPosa,
        tempo_lavoro: Math.max(15, formData.tempo_lavoro)
      });
    } else if (!tipoInfo?.hasPosa) {
      // Resetta tempo posa per servizi senza posa
      setFormData({ 
        ...formData, 
        tipo, 
        tempo_posa: 0,
        tempo_lavoro: formData.durata_minuti || 30
      });
    } else {
      setFormData({ ...formData, tipo });
    }
  };

  const filteredServizi = servizi.filter(s => {
    const matchSearch = s.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategoria = filterCategoria === "tutti" || s.categoria === filterCategoria;
    const matchTipo = filterTipo === "tutti" || s.tipo === filterTipo;
    return matchSearch && matchCategoria && matchTipo;
  });

  const serviziByCategoria = {
    donna: filteredServizi.filter(s => s.categoria === 'donna'),
    uomo: filteredServizi.filter(s => s.categoria === 'uomo'),
    unisex: filteredServizi.filter(s => s.categoria === 'unisex')
  };

  const formatDurata = (minuti) => {
    if (minuti < 60) return `${minuti} min`;
    const ore = Math.floor(minuti / 60);
    const min = minuti % 60;
    return min > 0 ? `${ore}h ${min}min` : `${ore}h`;
  };

  // Statistiche
  const stats = useMemo(() => {
    const totali = servizi.length;
    const attivi = servizi.filter(s => s.attivo).length;
    const conPosa = servizi.filter(s => (s.tempo_posa || 0) > 0).length;
    return { totali, attivi, conPosa };
  }, [servizi]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="servizi-page">
      {/* Header con statistiche - Responsive */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Scissors className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
            Servizi
          </h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-1">
            {stats.attivi} attivi su {stats.totali} • {stats.conPosa} con tempo di posa
          </p>
        </div>
      </div>

      {/* Info Card su Tempo Lavoro/Posa - Responsive */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg shrink-0">
              <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-blue-900 text-sm sm:text-base">Sistema Tempo Lavoro / Tempo Posa</h4>
              <p className="text-xs sm:text-sm text-blue-700 mt-1">
                <strong>Tempo Lavoro</strong>: minuti in cui l&apos;operatore lavora attivamente sul cliente.<br/>
                <strong>Tempo Posa</strong>: minuti in cui il colore/trattamento agisce (operatore libero per altri servizi).
              </p>
              <div className="flex flex-wrap gap-2 sm:gap-4 mt-2">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                  <Timer className="w-3 h-3 mr-1" /> Lavoro = Occupato
                </Badge>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                  <Hourglass className="w-3 h-3 mr-1" /> Posa = Disponibile
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtri e azioni - Responsive */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cerca servizio..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-servizi"
            />
          </div>
          <Select value={filterCategoria} onValueChange={setFilterCategoria}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              {CATEGORIE.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i tipi</SelectItem>
              {TIPI.map(tipo => (
                <SelectItem key={tipo.value} value={tipo.value}>
                  {tipo.label} {tipo.hasPosa && "⏱️"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Banner informativo regola servizi */}
        <div className="bg-gradient-to-r from-blue-50 to-pink-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
          <Info className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-slate-800">Regola Servizi in Agenda</p>
            <p className="text-slate-600">
              <span className="text-blue-600 font-medium">👨 Cliente Uomo</span> → Vede solo servizi <strong>Uomo + Unisex</strong> | 
              <span className="text-pink-600 font-medium ml-1">👩 Cliente Donna</span> → Vede solo servizi <strong>Donna + Unisex</strong>
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 btn-press" data-testid="add-servizio-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Servizio
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm max-h-[80vh] flex flex-col p-4">
              <DialogHeader className="shrink-0 pb-2">
                <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Playfair Display, serif' }}>
                  <Scissors className="w-4 h-4 text-blue-600" />
                  {editingServizio ? "Modifica Servizio" : "Nuovo Servizio"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-2 pr-1 -mr-1">
                {/* Nome */}
                <div>
                  <Label className="text-xs font-medium">Nome *</Label>
                  <Input
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Es: Colore Radici"
                    required
                    data-testid="input-nome"
                    className="h-8 text-sm"
                  />
                </div>
                
                {/* Categoria + Tipo in riga */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium">Categoria</Label>
                    <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIE.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Tipo</Label>
                    <Select value={formData.tipo} onValueChange={handleTipoChange}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPI.map(tipo => (
                          <SelectItem key={tipo.value} value={tipo.value}>
                            {tipo.label} {tipo.hasPosa && "⏱️"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sezione Tempo Lavoro e Posa - ULTRA Compatta */}
                <div className="border border-dashed rounded-lg p-2 space-y-2 bg-slate-50/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium flex items-center gap-1">
                      <Clock className="w-3 h-3 text-slate-500" />
                      Tempi
                    </span>
                    <Badge variant="outline" className="text-[10px] h-5">
                      {formatDurata(formData.tempo_lavoro + formData.tempo_posa)}
                    </Badge>
                  </div>

                  {/* Lavoro + Posa in griglia */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-emerald-600 font-medium">Lavoro</span>
                        <span className="text-[10px] font-bold text-emerald-600">{formData.tempo_lavoro}'</span>
                      </div>
                      <Slider
                        value={[formData.tempo_lavoro]}
                        onValueChange={([value]) => setFormData({ ...formData, tempo_lavoro: value })}
                        min={5}
                        max={120}
                        step={5}
                        className="[&_[role=slider]]:bg-emerald-500 [&_[role=slider]]:h-3 [&_[role=slider]]:w-3"
                        data-testid="slider-tempo-lavoro"
                      />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-amber-600 font-medium">Posa</span>
                        <span className="text-[10px] font-bold text-amber-600">{formData.tempo_posa}'</span>
                      </div>
                      <Slider
                        value={[formData.tempo_posa]}
                        onValueChange={([value]) => setFormData({ ...formData, tempo_posa: value })}
                        min={0}
                        max={60}
                        step={5}
                        className="[&_[role=slider]]:bg-amber-500 [&_[role=slider]]:h-3 [&_[role=slider]]:w-3"
                        data-testid="slider-tempo-posa"
                      />
                    </div>
                  </div>

                  {/* Barra grafica mini */}
                  <div className="flex h-2 rounded-full overflow-hidden bg-slate-200">
                    <div className="bg-emerald-500" style={{ width: `${(formData.tempo_lavoro / (formData.tempo_lavoro + formData.tempo_posa || 1)) * 100}%` }} />
                    {formData.tempo_posa > 0 && (
                      <div className="bg-amber-400" style={{ width: `${(formData.tempo_posa / (formData.tempo_lavoro + formData.tempo_posa || 1)) * 100}%` }} />
                    )}
                  </div>
                </div>

                {/* Prezzo + Colore in riga */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium flex items-center gap-1">
                      <Euro className="w-3 h-3" />
                      Prezzo €
                    </Label>
                    <Input
                      type="number"
                      step="0.50"
                      min="0"
                      value={formData.prezzo}
                      onChange={(e) => setFormData({ ...formData, prezzo: parseFloat(e.target.value) || 0 })}
                      required
                      data-testid="input-prezzo"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Colore</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {COLORI_SERVIZI.slice(0, 5).map(col => (
                        <button
                          key={col.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, colore: col.value })}
                          className={`w-5 h-5 rounded transition-all ${col.bg} ${
                            formData.colore === col.value 
                              ? 'ring-2 ring-offset-1 ring-slate-400 scale-110' 
                              : 'hover:scale-105'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {COLORI_SERVIZI.slice(5).map(col => (
                        <button
                          key={col.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, colore: col.value })}
                          className={`w-5 h-5 rounded transition-all ${col.bg} ${
                            formData.colore === col.value 
                              ? 'ring-2 ring-offset-1 ring-slate-400 scale-110' 
                              : 'hover:scale-105'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Servizi Associati - Mini */}
                <div>
                  <Label className="text-xs font-medium flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Servizi Associati
                  </Label>
                  <div className="border rounded p-1.5 max-h-16 overflow-y-auto space-y-0.5 mt-1">
                    {servizi.filter(s => s.id !== editingServizio?.id && s.attivo).slice(0, 6).map(s => (
                      <label 
                        key={s.id} 
                        className="flex items-center gap-1.5 p-0.5 hover:bg-slate-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.servizi_associati.includes(s.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ 
                                ...formData, 
                                servizi_associati: [...formData.servizi_associati, s.id] 
                              });
                            } else {
                              setFormData({ 
                                ...formData, 
                                servizi_associati: formData.servizi_associati.filter(id => id !== s.id) 
                              });
                            }
                          }}
                          className="rounded text-blue-600"
                        />
                        <span className="text-xs">{s.nome}</span>
                        <span className="text-[10px] text-slate-400 ml-auto">{s.durata_minuti}'</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Giorni Richiamo per Marketing */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-2 rounded-lg border border-purple-200">
                  <Label className="text-xs font-medium flex items-center gap-1 text-purple-700">
                    <Timer className="w-3 h-3" />
                    Giorni Richiamo (Marketing)
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      min="0"
                      max="365"
                      placeholder="0"
                      value={formData.giorni_richiamo || ""}
                      onChange={(e) => setFormData({ ...formData, giorni_richiamo: parseInt(e.target.value) || 0 })}
                      className="h-7 w-20 text-sm"
                    />
                    <span className="text-xs text-slate-500">giorni (0 = disattivato)</span>
                  </div>
                  <p className="text-[10px] text-purple-600 mt-1">
                    Dopo questi giorni, il cliente apparirà nella lista &quot;da richiamare&quot;
                  </p>
                </div>

                {/* Ordine Schema - Per sequenza esecuzione servizi */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-2 rounded-lg border border-blue-200">
                  <Label className="text-xs font-medium flex items-center gap-1 text-blue-700">
                    <Hash className="w-3 h-3" />
                    Ordine Schema (Sequenza Esecuzione)
                  </Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      placeholder="0"
                      value={formData.ordine_schema || ""}
                      onChange={(e) => setFormData({ ...formData, ordine_schema: parseInt(e.target.value) || 0 })}
                      className="h-7 w-16 text-sm"
                    />
                    <span className="text-xs text-slate-500">(es: 1=Colore, 2=Trattamento, 3=Piega)</span>
                  </div>
                  <p className="text-[10px] text-blue-600 mt-1">
                    Indica l&apos;ordine in cui il servizio viene eseguito (0 = non specificato)
                  </p>
                </div>

                {/* === EDITOR STEP MULTI-FASE === */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-2 rounded-lg border-2 border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs font-bold flex items-center gap-1 text-amber-800">
                      <Hash className="w-3 h-3" />
                      Struttura a Step (avanzata)
                    </Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-6 text-[10px] px-2 border-amber-400 text-amber-700 hover:bg-amber-100"
                      onClick={() => setFormData({
                        ...formData,
                        steps: [...(formData.steps || []), { nome: "", tipo: "lavoro", durata_minuti: 10 }]
                      })}
                      data-testid="btn-aggiungi-step"
                    >
                      + Step
                    </Button>
                  </div>
                  <p className="text-[10px] text-amber-700 mb-2">
                    Definisci le fasi reali del trattamento. Es: <span className="font-semibold">Applicazione 15&apos; (lavoro) → Posa 30&apos; → Lavaggio 10&apos; (lavoro) → Piega 15&apos; (lavoro)</span>. Se vuoto, si usano i campi Tempo Lavoro/Posa standard.
                  </p>

                  {(formData.steps || []).length === 0 && (
                    <p className="text-[10px] text-slate-400 text-center py-2 italic">Nessuno step definito — clicca &quot;+ Step&quot; per iniziare</p>
                  )}

                  <div className="space-y-1.5">
                    {(formData.steps || []).map((step, idx) => (
                      <div key={`step-${idx}`} className="flex items-center gap-1.5 bg-white p-1.5 rounded border border-amber-200" data-testid={`step-row-${idx}`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${
                          step.tipo === 'posa' ? 'bg-violet-500' : 'bg-blue-500'
                        }`}>
                          {idx + 1}
                        </span>
                        <Input
                          placeholder={step.tipo === 'posa' ? "Posa colore" : "Applicazione"}
                          value={step.nome || ""}
                          onChange={(e) => {
                            const newSteps = [...formData.steps];
                            newSteps[idx] = { ...newSteps[idx], nome: e.target.value };
                            setFormData({ ...formData, steps: newSteps });
                          }}
                          className="h-7 text-xs flex-1 min-w-0"
                          data-testid={`step-nome-${idx}`}
                        />
                        <select
                          value={step.tipo}
                          onChange={(e) => {
                            const newSteps = [...formData.steps];
                            newSteps[idx] = { ...newSteps[idx], tipo: e.target.value };
                            setFormData({ ...formData, steps: newSteps });
                          }}
                          className="h-7 text-xs border rounded px-1 bg-white"
                          data-testid={`step-tipo-${idx}`}
                        >
                          <option value="lavoro">🔵 Lavoro</option>
                          <option value="posa">🟣 Posa</option>
                        </select>
                        <Input
                          type="number"
                          min="1"
                          max="240"
                          value={step.durata_minuti || ""}
                          onChange={(e) => {
                            const newSteps = [...formData.steps];
                            newSteps[idx] = { ...newSteps[idx], durata_minuti: parseInt(e.target.value) || 0 };
                            setFormData({ ...formData, steps: newSteps });
                          }}
                          className="h-7 text-xs w-14"
                          placeholder="min"
                          data-testid={`step-durata-${idx}`}
                        />
                        <span className="text-[9px] text-slate-400 shrink-0">min</span>
                        {idx > 0 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
                            onClick={() => {
                              const newSteps = [...formData.steps];
                              [newSteps[idx - 1], newSteps[idx]] = [newSteps[idx], newSteps[idx - 1]];
                              setFormData({ ...formData, steps: newSteps });
                            }}
                            title="Sposta su"
                          >
                            ↑
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                          onClick={() => {
                            const newSteps = formData.steps.filter((_, i) => i !== idx);
                            setFormData({ ...formData, steps: newSteps });
                          }}
                          title="Rimuovi step"
                          data-testid={`step-rimuovi-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {(formData.steps || []).length > 0 && (() => {
                    const totLavoro = formData.steps.filter(s => s.tipo === 'lavoro').reduce((a, s) => a + (s.durata_minuti || 0), 0);
                    const totPosa = formData.steps.filter(s => s.tipo === 'posa').reduce((a, s) => a + (s.durata_minuti || 0), 0);
                    const totDurata = totLavoro + totPosa;
                    return (
                      <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-amber-200 text-[10px]">
                        <span className="text-blue-700">🔵 Lavoro: <b>{totLavoro}&apos;</b></span>
                        <span className="text-violet-700">🟣 Posa: <b>{totPosa}&apos;</b></span>
                        <span className="text-amber-700">⏱️ Totale: <b>{totDurata}&apos;</b></span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] px-2 border-amber-400"
                          onClick={() => setFormData({
                            ...formData,
                            durata_minuti: totDurata,
                            tempo_lavoro: totLavoro,
                            tempo_posa: totPosa
                          })}
                          title="Aggiorna i campi Tempo Lavoro/Posa/Durata dai totali degli step"
                          data-testid="btn-sync-step-totals"
                        >
                          Sincronizza
                        </Button>
                      </div>
                    );
                  })()}
                </div>

                {/* Switch attivo + Pulsanti FISSI */}
                <div className="sticky bottom-0 bg-white pt-2 border-t -mx-1 px-1">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Attivo</Label>
                    <Switch
                      checked={formData.attivo}
                      onCheckedChange={(checked) => setFormData({ ...formData, attivo: checked })}
                    />
                  </div>
                  <div className="flex gap-2">
                    {editingServizio && (
                      <Button 
                        type="button" 
                        variant="destructive" 
                        size="sm"
                        className="flex-1"
                        onClick={() => handleDelete(editingServizio)}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Elimina
                      </Button>
                    )}
                    <Button type="submit" size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="submit-servizio">
                      {editingServizio ? "Salva" : "Crea Servizio"}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Servizi Grid by Categoria */}
      <TooltipProvider>
        {CATEGORIE.map(cat => {
          const serviziCat = serviziByCategoria[cat.value];
          if (serviziCat.length === 0 && filterCategoria !== "tutti" && filterCategoria !== cat.value) return null;
          
          return (
            <div key={cat.value} className="space-y-3">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center`}>
                  <cat.icon className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {cat.label}
                </h3>
                <span className="text-sm text-slate-400">({serviziCat.length})</span>
              </div>
              
              {serviziCat.length === 0 ? (
                <p className="text-slate-400 text-sm pl-10">Nessun servizio in questa categoria</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {serviziCat.map(servizio => {
                    const tempoLavoro = servizio.tempo_lavoro || servizio.durata_minuti;
                    const tempoPosa = servizio.tempo_posa || 0;
                    const hasPosa = tempoPosa > 0;
                    
                    return (
                      <Card 
                        key={servizio.id} 
                        className={`group hover:shadow-md transition-all ${!servizio.attivo ? 'opacity-50 bg-slate-50' : ''}`}
                        data-testid={`servizio-${servizio.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <h4 className="font-medium text-slate-800 flex items-center gap-2">
                                {/* Pallino colore servizio */}
                                <span 
                                  className="w-3 h-3 rounded-full shrink-0 ring-1 ring-white shadow-sm"
                                  style={{ backgroundColor: servizio.colore || '#3B82F6' }}
                                  title={`Colore in agenda: ${servizio.colore || '#3B82F6'}`}
                                />
                                {servizio.nome}
                                {hasPosa && (
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Hourglass className="w-4 h-4 text-amber-500" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Include {tempoPosa} min di posa</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </h4>
                              <p className="text-xs text-slate-400 capitalize ml-5">{servizio.tipo}</p>
                            </div>
                            <Badge variant="outline" className={`${cat.color.replace('bg-', 'text-').replace('-500', '-600')} border-0 bg-opacity-10`}>
                              €{servizio.prezzo}
                            </Badge>
                          </div>

                          {/* Barra tempi */}
                          <div className="mb-3">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                              <span className="flex items-center gap-1">
                                <Timer className="w-3 h-3 text-emerald-500" />
                                {tempoLavoro} min lavoro
                              </span>
                              {hasPosa && (
                                <span className="flex items-center gap-1">
                                  <Hourglass className="w-3 h-3 text-amber-500" />
                                  {tempoPosa} min posa
                                </span>
                              )}
                            </div>
                            <div className="flex h-2 rounded-full overflow-hidden bg-slate-100">
                              <div 
                                className="bg-emerald-500"
                                style={{ width: `${(tempoLavoro / (tempoLavoro + tempoPosa)) * 100}%` }}
                              />
                              {hasPosa && (
                                <div 
                                  className="bg-amber-400"
                                  style={{ width: `${(tempoPosa / (tempoLavoro + tempoPosa)) * 100}%` }}
                                />
                              )}
                            </div>
                            <div className="flex justify-between items-center mt-1">
                              <p className="text-xs text-slate-400">
                                Totale: {formatDurata(tempoLavoro + tempoPosa)}
                              </p>
                              <div className="flex items-center gap-2">
                                {servizio.ordine_schema > 0 && (
                                  <span className="text-xs text-blue-600 flex items-center gap-0.5 bg-blue-50 px-1.5 py-0.5 rounded">
                                    <Hash className="w-3 h-3" />
                                    {servizio.ordine_schema}
                                  </span>
                                )}
                                {servizio.giorni_richiamo > 0 && (
                                  <span className="text-xs text-purple-600 flex items-center gap-0.5">
                                    <Timer className="w-3 h-3" />
                                    Richiamo {servizio.giorni_richiamo}gg
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-between items-center">
                            <Switch
                              checked={servizio.attivo}
                              onCheckedChange={() => toggleAttivo(servizio)}
                              className="data-[state=checked]:bg-emerald-500"
                            />
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditDialog(servizio)}
                              >
                                <Edit className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDelete(servizio.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </TooltipProvider>
    </div>
  );
}
